import { useState, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useNavigate } from 'react-router-dom';
import {
  Phone, Mail, MapPin, Calendar, FileText, User, CheckCircle, XCircle, PenTool, Printer,
  Hash, StickyNote, Send, Eye, ClipboardList, Pencil, Ban, Trash2,
} from 'lucide-react';
import { QuickViewSheetLayout } from '@/components/ui/QuickViewSheetLayout';
import { SaveStatusIndicator } from '@/components/ui/SaveStatusIndicator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useEstimate, useMarkEstimateAccepted, useMarkEstimateDeclined } from '@/hooks/useEstimates';
import { useLinkedCustomer } from '@/hooks/useCustomers';
import { CustomerLink } from '@/components/customers/CustomerLink';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useVisibility } from '@/hooks/useVisibility';
import { resolveTaxConfig, formatTaxLine } from '@/utils/tax';
import { isDateOnlyPast } from '@/utils/locale';
import { useSavingIndicator } from '@/hooks/useSavingIndicator';
import { cn } from '@/lib/utils';
import {
  CANCELLABLE_ESTIMATE_STATUSES,
  canDeleteEstimate,
  type EstimateLineItem,
} from '@/types/estimate';
import { EstimateActionConfirmDialog, type EstimateConfirmAction } from './EstimateActionConfirmDialog';
import { ViewSignatureDialog } from './ViewSignatureDialog';
import { EditEstimateDetailsDialog } from './EditEstimateDetailsDialog';
import { EstimateDepositPanel } from './EstimateDepositPanel';
import { EstimatePreview } from './EstimatePreview';
import { documentPageStyle } from '@/components/documents/print';

interface EstimateQuickViewSheetProps {
  estimateId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

const STATUS_BADGE_VARIANTS: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground border-transparent',
  sent: 'bg-info/10 text-info border-transparent',
  viewed: 'bg-chart-2/10 text-chart-2 border-transparent',
  accepted: 'bg-success/10 text-success border-transparent',
  declined: 'bg-destructive/10 text-destructive border-transparent',
  expired: 'bg-warning/10 text-warning border-transparent',
  cancelled: 'bg-muted text-muted-foreground border-transparent line-through',
};

function Fact({ icon: Icon, label, children }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="text-muted-foreground">{label}</span>
      <span className="ml-auto text-right">{children}</span>
    </div>
  );
}

export function EstimateQuickViewSheet({
  estimateId,
  open,
  onOpenChange,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
}: EstimateQuickViewSheetProps) {
  const navigate = useNavigate();
  const { data: estimate, isLoading, error } = useEstimate(estimateId);
  // Falls back to a phone match for estimates that never got a customer_id.
  const { data: linkedCustomer } = useLinkedCustomer(estimate);
  const { formatMoney, formatShortDate, formatLongDate, locale } = useCompanyLocale();
  const liveTax = useCompanyTax();
  const { canSeeCompanyFinancials } = useVisibility();
  const tax = resolveTaxConfig(estimate, liveTax);

  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [showEditDetails, setShowEditDetails] = useState(false);
  const [confirmAction, setConfirmAction] = useState<EstimateConfirmAction | null>(null);

  const markAccepted = useMarkEstimateAccepted();
  const markDeclined = useMarkEstimateDeclined();

  const acceptSaving = useSavingIndicator();
  const declineSaving = useSavingIndicator();

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: estimate?.estimate_number || 'Quotation',
    pageStyle: documentPageStyle(locale.paper),
  });

  const handleAccept = async () => {
    if (!estimateId) return;
    try {
      await acceptSaving.trackMutation(markAccepted.mutateAsync(estimateId));
    } catch {
      // Error handled by hook
    }
  };

  const handleDecline = async () => {
    if (!estimateId) return;
    try {
      await declineSaving.trackMutation(markDeclined.mutateAsync({ estimateId }));
    } catch {
      // Error handled by hook
    }
  };

  const canTakeAction = estimate?.status === 'draft' || estimate?.status === 'sent' || estimate?.status === 'viewed';
  const canCancel = !!estimate && CANCELLABLE_ESTIMATE_STATUSES.includes(estimate.status);
  const canDelete = !!estimate && canDeleteEstimate(estimate.status, canSeeCompanyFinancials);
  const lineItems: EstimateLineItem[] = estimate?.line_items || [];
  // Date-only comparison: valid_until is a calendar date, not an instant
  const isExpired = !!estimate && isDateOnlyPast(estimate.valid_until) && !['accepted', 'declined', 'cancelled'].includes(estimate.status);

  return (
    <QuickViewSheetLayout
      open={open}
      onOpenChange={onOpenChange}
      size="xl"
      title={estimate?.estimate_number || 'Estimate'}
      subtitle={estimate ? `${estimate.customer_name ?? ''}${estimate.lead?.lead_number ? ` · Lead ${estimate.lead.lead_number}` : ''}` : undefined}
      isLoading={isLoading}
      error={error as Error | null}
      isEmpty={!estimate && !isLoading && !error}
      onPrevious={onPrevious}
      onNext={onNext}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
      badges={
        estimate && (
          <>
            <Badge variant="outline" className={cn('capitalize', STATUS_BADGE_VARIANTS[estimate.status])}>
              {estimate.status}
            </Badge>
            {isExpired && (
              <Badge variant="outline" className="border-transparent bg-warning/10 text-warning">
                Expired
              </Badge>
            )}
          </>
        )
      }
      actions={
        estimate && (
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="outline" onClick={() => handlePrint()} className="gap-1">
              <Printer className="h-3.5 w-3.5" />
              PDF
            </Button>
            {canTakeAction && (
              <Button size="sm" variant="outline" onClick={() => setShowEditDetails(true)} className="gap-1">
                <Pencil className="h-3.5 w-3.5" />
                Edit details
              </Button>
            )}
            {canTakeAction && !isExpired && (
              <>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={handleAccept}
                    disabled={acceptSaving.status === 'saving' || declineSaving.status === 'saving'}
                    className="gap-1"
                  >
                    <CheckCircle className="h-3.5 w-3.5" />
                    Accept
                  </Button>
                  <SaveStatusIndicator status={acceptSaving.status} error={acceptSaving.error} />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDecline}
                    disabled={acceptSaving.status === 'saving' || declineSaving.status === 'saving'}
                    className="gap-1"
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Decline
                  </Button>
                  <SaveStatusIndicator status={declineSaving.status} error={declineSaving.error} />
                </div>
              </>
            )}
            {canCancel && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmAction('cancel')}
                className="gap-1 text-warning hover:text-warning"
              >
                <Ban className="h-3.5 w-3.5" />
                Cancel
              </Button>
            )}
            {canDelete && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmAction('delete')}
                className="gap-1 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </Button>
            )}
          </div>
        )
      }
    >
      {estimate && (
        <div className="space-y-4">
          {/* Headline amount */}
          <div className="rounded-lg bg-primary/10 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-primary">Quoted total</p>
            <p className="mt-0.5 text-2xl font-semibold tabular-nums tracking-tight">
              {formatMoney(estimate.estimate_amount)}
            </p>
            <p className={cn('mt-0.5 text-xs', isExpired ? 'font-medium text-destructive' : 'text-muted-foreground')}>
              {estimate.valid_until ? `Valid until ${formatShortDate(estimate.valid_until)}` : 'No expiry set'}
            </p>
          </div>

          {/* Customer */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-foreground">
              <User className="h-4 w-4 shrink-0 text-muted-foreground" />
              <CustomerLink
                customerId={linkedCustomer?.id ?? estimate.customer_id}
                name={estimate.customer_name}
                className="font-medium"
                onNavigate={() => onOpenChange(false)}
              />
              {estimate.customer?.company_name && (
                <span className="text-sm text-muted-foreground">· {estimate.customer.company_name}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
              {estimate.customer_phone && (
                <a
                  href={`tel:${estimate.customer_phone}`}
                  className="flex items-center gap-1.5 text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Phone className="h-3.5 w-3.5" />
                  {estimate.customer_phone}
                </a>
              )}
              {estimate.sent_to_email && (
                <a
                  href={`mailto:${estimate.sent_to_email}`}
                  className="flex items-center gap-1.5 text-muted-foreground hover:text-primary"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Mail className="h-3.5 w-3.5" />
                  {estimate.sent_to_email}
                </a>
              )}
            </div>
            {estimate.customer_address && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">{estimate.customer_address}</span>
              </div>
            )}
          </div>

          {/* Key facts */}
          <div className="grid grid-cols-1 gap-2 border-t pt-3 sm:grid-cols-2">
            <Fact icon={Calendar} label="Created">{formatShortDate(estimate.created_at)}</Fact>
            <Fact icon={Calendar} label="Valid until">
              <span className={isExpired ? 'font-medium text-destructive' : ''}>
                {estimate.valid_until ? formatShortDate(estimate.valid_until) : '—'}
              </span>
            </Fact>
            {estimate.lead?.lead_number && (
              <Fact icon={ClipboardList} label="Lead">
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => { onOpenChange(false); navigate(`/leads/${estimate.lead_id}`); }}
                >
                  {estimate.lead.lead_number}
                </button>
              </Fact>
            )}
            {estimate.customer?.customer_number && (
              <Fact icon={Hash} label="Customer no.">{estimate.customer.customer_number}</Fact>
            )}
            {estimate.creator?.full_name && (
              <Fact icon={User} label="Prepared by">{estimate.creator.full_name}</Fact>
            )}
            {estimate.sent_at && (
              <Fact icon={Send} label="Sent">{formatShortDate(estimate.sent_at)}</Fact>
            )}
            {estimate.status === 'viewed' && (
              <Fact icon={Eye} label="Customer viewed">Yes</Fact>
            )}
          </div>

          {/* Scope */}
          {estimate.service_description && (
            <div className="border-t pt-3 text-sm">
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Scope of work</p>
              <p className="whitespace-pre-wrap text-muted-foreground">{estimate.service_description}</p>
            </div>
          )}

          {/* Line items */}
          {lineItems.length > 0 && (
            <div className="border-t pt-3">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                <FileText className="h-4 w-4 text-muted-foreground" />
                Line items
              </div>
              <div className="space-y-1.5 text-sm">
                {lineItems.map((item, index) => (
                  <div key={index} className="flex justify-between gap-3">
                    <span className="min-w-0 flex-1 text-muted-foreground">
                      {item.description}
                      {item.quantity !== 1 && (
                        <span className="ml-1 text-xs tabular-nums">× {item.quantity}</span>
                      )}
                    </span>
                    <span className="shrink-0 tabular-nums">{formatMoney(item.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Financial summary */}
          <div className="space-y-1 border-t pt-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="tabular-nums">{formatMoney(estimate.subtotal)}</span>
            </div>
            {tax.enabled && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{formatTaxLine(tax)}</span>
                <span className="tabular-nums">{formatMoney(estimate.gst_amount)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold">
              <span>Total</span>
              <span className="tabular-nums">{formatMoney(estimate.estimate_amount)}</span>
            </div>
          </div>

          <EstimateDepositPanel estimate={estimate} />

          {/* Terms */}
          {estimate.terms && (
            <div className="border-t pt-3 text-sm">
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Terms & conditions</p>
              <p className="whitespace-pre-wrap text-muted-foreground">{estimate.terms}</p>
            </div>
          )}

          {/* Outcome */}
          {estimate.status === 'accepted' && (
            <div className="rounded-lg bg-success/10 px-3 py-2.5 text-sm">
              <div className="flex items-center gap-2 text-success">
                <CheckCircle className="h-4 w-4" />
                <span>
                  {estimate.signer_name ? <>Signed by <strong>{estimate.signer_name}</strong></> : 'Accepted'}
                </span>
              </div>
              {(estimate.signed_at || estimate.accepted_at) && (
                <p className="mt-1 text-muted-foreground">
                  {formatLongDate(estimate.signed_at ?? estimate.accepted_at)}
                </p>
              )}
              {estimate.signer_name && (
                <Button variant="outline" size="sm" className="mt-2 gap-1" onClick={() => setShowSignatureDialog(true)}>
                  <PenTool className="h-3.5 w-3.5" />
                  View signature &amp; audit trail
                </Button>
              )}
            </div>
          )}
          {estimate.status === 'declined' && (
            <div className="rounded-lg bg-destructive/10 px-3 py-2.5 text-sm">
              <div className="flex items-center gap-2 text-destructive">
                <XCircle className="h-4 w-4" />
                <span>
                  {estimate.declined_by === 'customer'
                    ? <>Declined by customer{estimate.decliner_name ? <> (<strong>{estimate.decliner_name}</strong>)</> : null}</>
                    : estimate.declined_by === 'staff' ? 'Marked declined by staff' : 'Declined'}
                  {estimate.declined_at ? ` on ${formatShortDate(estimate.declined_at)}` : ''}
                </span>
              </div>
              {estimate.decline_reason && (
                <p className="mt-1 text-muted-foreground">Reason: {estimate.decline_reason}</p>
              )}
            </div>
          )}
          {estimate.status === 'cancelled' && (
            <div className="rounded-lg bg-muted px-3 py-2.5 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Ban className="h-4 w-4" />
                <span>
                  Cancelled by staff{estimate.cancelled_at ? ` on ${formatShortDate(estimate.cancelled_at)}` : ''}. The customer link no longer works.
                </span>
              </div>
            </div>
          )}

          {/* Internal notes — staff only, never printed or emailed */}
          {estimate.notes && (
            <div className="rounded-lg bg-warning/10 px-3 py-2 text-sm">
              <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-warning">
                <StickyNote className="h-3.5 w-3.5" />
                Internal notes
              </p>
              <p className="whitespace-pre-wrap text-foreground/80">{estimate.notes}</p>
            </div>
          )}
        </div>
      )}

      <ViewSignatureDialog
        open={showSignatureDialog}
        onOpenChange={setShowSignatureDialog}
        estimate={estimate || null}
      />
      <EditEstimateDetailsDialog
        open={showEditDetails}
        onOpenChange={setShowEditDetails}
        estimate={estimate || null}
      />
      <EstimateActionConfirmDialog
        action={confirmAction}
        estimate={estimate || null}
        onOpenChange={(next) => !next && setConfirmAction(null)}
        onDone={(action) => {
          if (action === 'delete') onOpenChange(false);
        }}
      />

      {/* Hidden print target */}
      {estimate && (
        <div className="hidden print:block">
          <EstimatePreview ref={printRef} estimate={estimate} />
        </div>
      )}
    </QuickViewSheetLayout>
  );
}
